import { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Camera, CheckCircle2, RefreshCcw, XCircle } from 'lucide-react';
import { API_ORIGIN } from '../../lib/api';
import { PlyEZMark } from '../../components/BrandLogo';

interface ScanSession {
  status: 'pending' | 'captured' | 'expired';
  workspaceId: string;
  expiresAt: string;
}

/**
 * Mobile receipt-capture page. No auth — the token in the URL IS the
 * auth. The visitor (typically the desktop user themselves, having
 * scanned the QR with their phone) sees a single big "take photo"
 * button that triggers the native camera via `capture="environment"`
 * on the file input.
 *
 * Once the file is selected we POST it back to /api/m/scan/:token. The
 * gateway emits `mobile-scan:captured` so the desktop sees the upload
 * land without a refresh.
 */
export function MobileScanPage() {
  const { token = '' } = useParams<{ token: string }>();
  const { t } = useTranslation();
  const [session, setSession] = useState<ScanSession | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    axios.get<ScanSession>(`${API_ORIGIN}/api/m/scan/${token}`)
      .then((r) => setSession(r.data))
      .catch(() => setErr(t('mobileScan.invalid')));
  }, [token, t]);

  async function onPick(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', f, f.name);
      await axios.post(`${API_ORIGIN}/api/m/scan/${token}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDone(true);
    } catch (er: unknown) {
      const msg = (er as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setErr(typeof msg === 'string' ? msg : t('mobileScan.uploadError'));
    } finally {
      setBusy(false);
    }
  }

  const expired = session?.status === 'expired';
  const captured = session?.status === 'captured' || done;

  return (
    <div className="washi min-h-screen bg-background flex flex-col">
      <header className="px-5 py-4 border-b border-border bg-card/80 backdrop-blur flex items-center gap-2">
        <PlyEZMark size={22} />
        <span className="display text-base font-semibold">{t('brand')}</span>
        <span className="text-xs text-muted-foreground ml-1">· {t('mobileScan.title')}</span>
      </header>

      <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
        {err ? (
          <div className="text-center max-w-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-warn-50 text-warn-600 dark:bg-warn-600/15 dark:text-amber-200">
              <XCircle size={28} />
            </div>
            <h1 className="display text-xl font-semibold">{t('mobileScan.errorTitle')}</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{err}</p>
          </div>
        ) : expired ? (
          <div className="text-center max-w-sm">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-muted text-muted-foreground">
              <RefreshCcw size={28} />
            </div>
            <h1 className="display text-xl font-semibold">{t('mobileScan.expiredTitle')}</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t('mobileScan.expiredBody')}</p>
          </div>
        ) : captured ? (
          <div className="text-center max-w-sm">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-300">
              <CheckCircle2 size={36} />
            </div>
            <h1 className="display text-2xl font-semibold">{t('mobileScan.doneTitle')}</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t('mobileScan.doneBody')}</p>
          </div>
        ) : (
          <div className="text-center max-w-sm">
            <span className="inline-flex items-center gap-2 rounded-full pill-pearl px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em]">
              <span className="sparkle-dot" aria-hidden />
              {t('mobileScan.eyebrow')}
            </span>
            <h1 className="display text-2xl font-semibold mt-4">{t('mobileScan.takeTitle')}</h1>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{t('mobileScan.takeBody')}</p>
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPick}
              className="hidden"
            />
            <button
              type="button"
              disabled={busy}
              onClick={() => fileRef.current?.click()}
              className="mt-10 inline-flex items-center gap-2 px-7 h-14 rounded-2xl bg-accent-600 text-white font-medium text-base shadow-washi transition-opacity hover:opacity-95 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            >
              <Camera size={20} aria-hidden /> {busy ? t('mobileScan.uploading') : t('mobileScan.cta')}
            </button>
            <p className="mt-6 text-[11px] text-muted-foreground">{t('mobileScan.footer')}</p>
          </div>
        )}
      </main>
    </div>
  );
}
